import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableItemActionRouterLink,
  TableService,
} from '@web/shared/ui';
import { RouteProviderService, RouterLink } from '@web/common';
import { AdminBannerListAction } from '../../enums/admin-banner-list-action';
import { AdminBannerListItemAction } from '../../enums/admin-banner-list-item-action';
import { Nillable, once } from '@web/utils';
import { of } from 'rxjs';
import { AdminBannerCollectionItem } from '../../models/admin-banner-collection-item';
import { AdminBannerCollectionFilter } from '../../models/admin-banner-collection-filter';
import { AdminBannerDataSourceService } from '../../services/admin-banner-data-source.service';
import { AdminBannerStatusProviderService } from '../../providers/admin-banner-status-provider.service';
import { AdminBannerListCustomCellTemplateComponent } from '../../components/admin-banner-list-custom-cell-template/admin-banner-list-custom-cell-template.component';
import { BannerLocationTypeProviderService } from '../../../shared/providers/banner-location-type-provider.service';
import { BannerLocationType } from '../../../shared/enums/banner-location-type';

@Component({
  selector: 'app-admin-banner-list',
  imports: [TableComponent, AdminBannerListCustomCellTemplateComponent],
  templateUrl: './admin-banner-list.component.html',
  styleUrl: './admin-banner-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBannerListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminBannerCollectionItem, AdminBannerCollectionFilter, AdminBannerListItemAction, never, AdminBannerListAction>).create();
  private readonly bannerDataSource = inject(AdminBannerDataSourceService);
  private readonly table = inject(TableService);
  private readonly statusProvider = inject(AdminBannerStatusProviderService);
  private readonly bannerLocationProvider = inject(
    BannerLocationTypeProviderService,
  );

  public readonly createBannerVisibility = input<boolean>(false);
  public readonly location = input<Nillable<BannerLocationType>>(null);
  public readonly allowedLocations = input<BannerLocationType[]>([
    BannerLocationType.HomePageHeader,
    BannerLocationType.SubscribePageHeader,
  ]);
  /* prettier-ignore */
  public readonly editRouterLinkFactory = input.required<(provider: RouteProviderService) => TableItemActionRouterLink<AdminBannerCollectionItem>>();
  /* prettier-ignore */
  public readonly createRouterLinkFactory = input.required<(provider: RouteProviderService) => RouterLink>();
  /* prettier-ignore */
  public readonly previewRouterLinkFactory = input.required<(provider: RouteProviderService) => TableItemActionRouterLink<AdminBannerCollectionItem>>();

  protected config!: TableConfig<
    AdminBannerCollectionItem,
    AdminBannerCollectionFilter,
    AdminBannerListItemAction,
    never,
    AdminBannerListAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    const showLocationColumn = this.location() === null;
    const location = this.location();

    this.config = this.builder
      .dataKey('id')
      .dataSource({
        fetch: (lazyLoad) =>
          location
            ? this.bannerDataSource.fetchCollectionByLocation({
                lazyLoad,
                location,
              })
            : this.bannerDataSource.fetchHeaderCollection({ lazyLoad }),
      })
      .filters((builder) => [
        builder.initText('url').build(),
        builder
          .initSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),
        ...(showLocationColumn
          ? [
              builder
                .initSelect('location')
                .dictionary(() =>
                  this.bannerLocationProvider.selectItemsFor(
                    this.allowedLocations(),
                  ),
                )
                .build(),
            ]
          : []),
        builder.initDateRange('createdAt').build(),
        builder.initDateTimeRange('visibilityStartDate').build(),
        builder.initDateTimeRange('visibilityEndDate').build(),
      ])
      .columns((builder) => [
        builder //
          .initCustom('file')
          .width('8rem')
          .label('Zdjęcie')
          .build(),

        builder //
          .initCustom('url')
          .filterable(false)
          .sortable(false)
          .label('Adres URL')
          .width('30rem')
          .build(),
        ...(showLocationColumn
          ? [
              builder //
                .initCustom('location')
                .filterKey('location')
                .label('Lokalizacja')
                .sortKey('location')
                .width('14rem')
                .build(),
            ]
          : []),

        builder //
          .initCustom('status')
          .filterKey('status')
          .sortKey('status')
          .width('20rem')
          .label('Status')
          .build(),

        builder //
          .initDateTime('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .label('Data utworzenia')
          .build(),

        builder //
          .initDateTime('visibilityStartDate')
          .filterKey('visibilityStartDate')
          .sortKey('visibilityStartDate')
          .label('Data rozpoczęcia')
          .build(),

        builder //
          .initDateTime('visibilityEndDate')
          .filterKey('visibilityEndDate')
          .sortKey('visibilityEndDate')
          .label('Data zakończenia')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(AdminBannerListAction.Create)
          .label('Dodaj baner')
          .visibility(() => of(this.createBannerVisibility()))
          .routerLink((provider) => {
            const factory = this.createRouterLinkFactory();

            return factory(provider);
          })
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminBannerListItemAction.Preview)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .visibility((item) => item.canBeViewed)
          .routerLink((provider) => {
            const factory = this.previewRouterLinkFactory();

            return factory(provider);
          })
          .build(),

        builder
          .initRouterLink(AdminBannerListItemAction.Edit)
          .icon(Icon.Pencil)
          .tooltip('Edytuj')
          .visibility((item) => item.canBeEdited)
          .routerLink((provider) => {
            const factory = this.editRouterLinkFactory();

            return factory(provider);
          })
          .build(),

        builder
          .initCommand(AdminBannerListItemAction.Remove)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .visibility((item) => item.canBeRemoved)
          .command((item) =>
            (location
              ? this.bannerDataSource.removeByIdAndLocation({
                  id: item.id,
                  location,
                })
              : this.bannerDataSource.removeHeaderById({ id: item.id })
            )
              .pipe(once())
              .subscribe(() => this.refresh()),
          )
          .build(),
      ])
      .build();
  }

  private refresh(): void {
    this.table.refresh();
  }
}
